import { create } from 'zustand';
import { Tournament } from '@/types/cricket';
import { 
  fetchTournaments, 
  createTournament, 
  updateTournament as updateTournamentAPI,
  deleteTournament as deleteTournamentAPI
} from '@/services/supabaseService';
import { toast } from '@/components/ui/use-toast';

interface TournamentStore {
  tournaments: Tournament[];
  activeTournament: string | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchTournaments: () => Promise<void>;
  addTournament: (tournament: Omit<Tournament, 'id' | 'teams'> & { logoFile?: File | null }) => Promise<void>;
  updateTournament: (tournament: Tournament) => Promise<void>;
  deleteTournament: (tournamentId: string) => Promise<void>;
  setActiveTournament: (tournamentId: string | null) => void;
}

const useTournamentStore = create<TournamentStore>((set, get) => ({
  tournaments: [],
  activeTournament: null,
  loading: false,
  error: null,
  
  fetchTournaments: async () => {
    set({ loading: true, error: null });
    try {
      const tournamentsData = await fetchTournaments();
      set({ tournaments: tournamentsData, loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error fetching tournaments:", error);
      set({ 
        error: message || "Failed to load tournaments", 
        loading: false 
      });
      toast({
        title: "Error",
        description: "Failed to load tournaments",
        variant: "destructive",
      });
    }
  },
  
  addTournament: async (tournament) => {
    set({ loading: true });
    try {
      const newTournament = await createTournament(tournament);
      set((state) => ({ tournaments: [...state.tournaments, newTournament] }));
      toast({
        title: "Tournament created successfully!",
        variant: "default"
      });
    } catch (error) {
      console.error("Error adding tournament:", error);
      toast({
        title: "Failed to create tournament.",
        variant: "destructive"
      });
    } finally {
      set({ loading: false });
    }
  },

  updateTournament: async (tournament) => {
    try {
      const updatedTournament = await updateTournamentAPI(tournament);
      set((state) => ({
        tournaments: state.tournaments.map((t) =>
          t.id === tournament.id ? updatedTournament : t
        ),
      }));
      toast({
        title: 'Success',
        description: `${updatedTournament.name} updated successfully!`,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Error updating tournament:', error);
      toast({
        title: 'Error',
        description: message || 'Failed to update tournament',
        variant: 'destructive',
      });
      throw error;
    }
  },

  deleteTournament: async (tournamentId) => {
    try {
      await deleteTournamentAPI(tournamentId);
      set((state) => ({
        tournaments: state.tournaments.filter((t) => t.id !== tournamentId),
      }));
      toast({
        title: "Success",
        description: "Tournament deleted successfully!",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error("Error deleting tournament:", error);
      toast({
        title: "Error",
        description: message || "Failed to delete tournament",
        variant: "destructive",
      });
      throw error;
    }
  },

  setActiveTournament: (tournamentId) => set({ activeTournament: tournamentId }),
}));

export default useTournamentStore; 